type ThreadableItemBase = {
  content?: string;
  createdAt: string;
  messageId: string;
  threadId?: string;
};

export type WithSubRows<T> = T & { subRows?: T[] };

/**
 * Takes all the messages, look at their threadId and remove all the children from the initial array to move them in the subrows
 */
export function nestAdBoardDataFeedByThread<T extends ThreadableItemBase>(
  data: T[],
  participantView: boolean
): WithSubRows<T>[] {
  // sort data by created time - NOTE parent messages will always be created before their threaded children!
  const sortedData = [...data].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  const byId = new Map<string, WithSubRows<T>>();
  const rootItems: WithSubRows<T>[] = [];

  for (const item of sortedData) {
    const itemWithSub: WithSubRows<T> = { ...item };

    if (item.threadId) {
      // fetch parent of threaded message
      const parent = byId.get(item.threadId);
      const itemWithParentMessageContent = {
        ...itemWithSub,
        parentMessageContent: parent?.content,
      };

      if (parent) {
        // initialize subrows if non existant
        if (!parent.subRows) parent.subRows = [];
        // push threaded message to subrow
        parent.subRows.push(itemWithParentMessageContent);
      }
      // does the same thing (need subrows to count the replies) but keep the childen in the parent array because we look the thread in the modal
      if (participantView) {
        // if we're in the participant view, we show all threaded messages as main row items
        // because the modal will show the full threads
        rootItems.push(itemWithParentMessageContent);
        byId.set(item.messageId, itemWithParentMessageContent);
      }
    } else {
      // otherwise, the message is a parent message, so we push it to root items
      rootItems.push(itemWithSub);
      byId.set(item.messageId, itemWithSub);
    }
  }

  rootItems.forEach((item) => {
    if (item.subRows) {
      item.subRows.sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
    }
  });

  return rootItems;
}

/**
 * Depending on the view (single participant view, thread in a modal view...)
 * we define if the row of the messageId should be expanded or not
 */
export function getDefaultExpandedState(
  data: WithSubRows<ThreadableItemBase>[],
  participantView: boolean,
  threadedModalView: boolean
): Record<string, boolean> {
  // expanded state variable in @tanstack/react-table expects Record<string, boolean>
  const expanded: Record<string, boolean> = {};

  for (const item of data) {
    // no children should be displayed
    if (participantView) {
      expanded[item.messageId] = false;
      continue;
    }
    if (threadedModalView) {
      expanded[item.messageId] = true;
      continue;
    }
    if (item.subRows?.length && item.subRows.length <= 3) {
      expanded[item.messageId] = true;
    }
  }

  return expanded;
}
